import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Layers,
  Loader2,
  Search,
  ShieldCheck,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  applyBatchCorrection,
  BatchApplyResult,
  BatchIntent,
  BatchOperation,
  BatchPool,
  BatchPoolsResponse,
  BatchPreview,
  getBatchPools,
  previewBatchCorrection,
} from "@/Api/balanceAdjustments";
import { getSelectSellers } from "@/Api/sellers";
import { ContestOption, SellerOption, toOptions, toSellerOptions } from "../types";
import { GHOST_BUTTON_CLASS } from "../styles";

const OPERATION_LABELS: Record<BatchOperation, string> = {
  none: "Não alterar",
  set: "Definir",
  add: "Adicionar",
  subtract: "Retirar",
};

const OPERATIONS: BatchOperation[] = ["none", "set", "add", "subtract"];

/** Valores de balanço são quantidades inteiras de cotas. */
const parseAmount = (value: string): number | null => {
  if (value.trim() === "") return null;
  const parsed = Number(value);

  return Number.isInteger(parsed) ? parsed : NaN;
};

interface FieldConfig {
  operation: BatchOperation;
  amount: string;
}

const EMPTY_FIELD: FieldConfig = { operation: "none", amount: "" };

/**
 * Correção em lote do balanço de vendedor.
 *
 * Uma única configuração é aplicada a todos os bolões selecionados. O frontend
 * valida antes de enviar, mas quem manda é o backend: ele relê os valores
 * atuais, recalcula os resultados e só aceita a execução com a assinatura do
 * resumo revisado.
 */
export const BatchCorrectionTab = ({ contests }: { contests: ContestOption[] }) => {
  const [sellers, setSellers] = useState<SellerOption[]>([]);
  const [sellerIds, setSellerIds] = useState<string[]>([]);
  const [contestIds, setContestIds] = useState<string[]>([]);

  const [poolsData, setPoolsData] = useState<BatchPoolsResponse | null>(null);
  const [selectedPools, setSelectedPools] = useState<number[]>([]);

  const [balanceConfig, setBalanceConfig] = useState<FieldConfig>(EMPTY_FIELD);
  const [withHeldConfig, setWithHeldConfig] = useState<FieldConfig>(EMPTY_FIELD);

  const [preview, setPreview] = useState<BatchPreview | null>(null);
  const [result, setResult] = useState<BatchApplyResult | null>(null);

  const [isLoadingPools, setIsLoadingPools] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    const loadSellers = async () => {
      const response = await getSelectSellers({});
      if (response.ok) setSellers(response.data?.response ?? []);
    };

    loadSellers();
  }, []);

  const sellerOptions = useMemo(() => toSellerOptions(sellers), [sellers]);
  const sellerId = sellerIds[0];
  const contestId = contestIds[0];

  const resetDownstream = () => {
    setPoolsData(null);
    setSelectedPools([]);
    setPreview(null);
    setResult(null);
    setIsConfirming(false);
  };

  const handleSingleSelect =
    (setter: (values: string[]) => void, current: string[]) => (values: string[]) => {
      const added = values.find((value) => !current.includes(value));
      setter(added ? [added] : values.slice(0, 1));
      resetDownstream();
    };

  const handleLoadPools = async () => {
    if (!sellerId || !contestId) return;

    setIsLoadingPools(true);
    resetDownstream();

    const response = await getBatchPools(sellerId, contestId);

    setIsLoadingPools(false);

    if (!response.ok) return;

    const data: BatchPoolsResponse = response.data?.response;
    setPoolsData(data);

    if (data.pools.length === 0) {
      toast.info("Este vendedor não possui balanço neste concurso.");
    }
  };

  const selectablePools = useMemo(
    () => (poolsData?.pools ?? []).filter((pool) => pool.selectable),
    [poolsData]
  );

  const togglePool = (pool: BatchPool) => {
    if (!pool.selectable) return;

    setPreview(null);
    setResult(null);
    setIsConfirming(false);
    setSelectedPools((current) =>
      current.includes(pool.cp_classification_id)
        ? current.filter((id) => id !== pool.cp_classification_id)
        : [...current, pool.cp_classification_id]
    );
  };

  const handleSelectAll = () => {
    setPreview(null);
    setResult(null);
    setSelectedPools(selectablePools.map((pool) => pool.cp_classification_id));
  };

  const handleClearSelection = () => {
    setPreview(null);
    setResult(null);
    setSelectedPools([]);
  };

  const updateConfig =
    (setter: (value: FieldConfig) => void, current: FieldConfig) =>
    (patch: Partial<FieldConfig>) => {
      setter({ ...current, ...patch });
      setPreview(null);
      setResult(null);
      setIsConfirming(false);
    };

  /** Validação local. O backend revalida tudo de qualquer forma. */
  const validationError = useMemo(() => {
    if (!sellerId) return "Selecione o vendedor.";
    if (!contestId) return "Selecione o concurso.";
    if (!poolsData) return "Carregue os bolões do vendedor.";
    if (poolsData.pools.length === 0) return "Não há bolões com balanço para corrigir.";
    if (selectedPools.length === 0) return "Selecione ao menos um bolão.";

    if (balanceConfig.operation === "none" && withHeldConfig.operation === "none") {
      return "Selecione ao menos uma operação: balanço ou valor preso.";
    }

    const fields: [string, FieldConfig][] = [
      ["balanço", balanceConfig],
      ["valor preso", withHeldConfig],
    ];

    for (const [label, config] of fields) {
      if (config.operation === "none") continue;

      const amount = parseAmount(config.amount);

      if (amount === null) return `Informe o valor do ${label}.`;
      if (Number.isNaN(amount)) return `O valor do ${label} deve ser um número inteiro.`;
      if (amount < 0) return `O valor do ${label} não pode ser negativo.`;
      if (config.operation !== "set" && amount === 0) {
        return `Para adicionar ou retirar, o valor do ${label} precisa ser maior que zero.`;
      }
    }

    return null;
  }, [sellerId, contestId, poolsData, selectedPools, balanceConfig, withHeldConfig]);

  const buildIntent = (): BatchIntent => ({
    seller_id: sellerId,
    contest_id: contestId,
    cp_classification_ids: selectedPools,
    balance_operation: balanceConfig.operation,
    balance_amount:
      balanceConfig.operation === "none" ? null : parseAmount(balanceConfig.amount),
    with_held_operation: withHeldConfig.operation,
    with_held_amount:
      withHeldConfig.operation === "none" ? null : parseAmount(withHeldConfig.amount),
  });

  const handlePreview = async () => {
    if (validationError) {
      toast.error(validationError);

      return;
    }

    setIsPreviewing(true);
    setResult(null);

    const response = await previewBatchCorrection(buildIntent());

    setIsPreviewing(false);

    if (!response.ok) {
      setPreview(null);

      return;
    }

    setPreview(response.data?.response);
  };

  const handleApply = async () => {
    if (!preview) return;

    setIsApplying(true);

    const response = await applyBatchCorrection({
      ...buildIntent(),
      preview_hash: preview.preview_hash,
      confirmed: true,
    });

    setIsApplying(false);
    setIsConfirming(false);

    if (!response.ok) {
      /** Resumo pode ter ficado obsoleto: obriga a refazer. */
      setPreview(null);

      return;
    }

    setResult(response.data?.response);
    setPreview(null);
    toast.success("Correção em lote aplicada e validada.");

    await handleLoadPoolsSilently();
  };

  const handleLoadPoolsSilently = async () => {
    if (!sellerId || !contestId) return;

    const response = await getBatchPools(sellerId, contestId);
    if (response.ok) setPoolsData(response.data?.response);
    setSelectedPools([]);
  };

  const renderFieldConfig = (
    label: string,
    config: FieldConfig,
    setter: (value: FieldConfig) => void
  ) => {
    const update = updateConfig(setter, config);

    return (
      <div className="space-y-3 rounded-md border p-4">
        <div className="text-sm font-medium">{label}</div>

        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Operação</Label>
          <Select
            value={config.operation}
            onValueChange={(value) => update({ operation: value as BatchOperation })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OPERATIONS.map((operation) => (
                <SelectItem key={operation} value={operation}>
                  {OPERATION_LABELS[operation]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Valor (cotas)</Label>
          <Input
            type="number"
            step={1}
            min={0}
            value={config.amount}
            disabled={config.operation === "none"}
            placeholder={config.operation === "none" ? "—" : "0"}
            onChange={(event) => update({ amount: event.target.value })}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Escopo</CardTitle>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Vendedor</Label>
              <MultiSelectCheckbox
                options={sellerOptions}
                selectedValues={sellerIds}
                hideBadge
                onSelectionChange={handleSingleSelect(setSellerIds, sellerIds)}
                placeholder="Selecione o vendedor"
                searchable
                searchPlaceholder="Buscar vendedor..."
              />
            </div>

            <div className="space-y-1">
              <Label>Concurso</Label>
              <MultiSelectCheckbox
                options={toOptions(contests)}
                selectedValues={contestIds}
                hideBadge
                onSelectionChange={handleSingleSelect(setContestIds, contestIds)}
                placeholder="Selecione o concurso"
                searchable
                searchPlaceholder="Buscar concurso..."
              />
            </div>
          </div>

          <div className="flex justify-end border-t pt-4">
            <Button
              size="sm"
              onClick={handleLoadPools}
              disabled={!sellerId || !contestId || isLoadingPools}
            >
              {isLoadingPools ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Search className="mr-2 h-4 w-4" />
              )}
              Buscar bolões
            </Button>
          </div>
        </CardContent>
      </Card>

      {poolsData && poolsData.warnings.length > 0 && (
        <div className="flex flex-col gap-2">
          {poolsData.warnings.map((warning) => (
            <div
              key={warning}
              className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{warning}</span>
            </div>
          ))}
        </div>
      )}

      {poolsData && poolsData.pools.length > 0 && (
        <Card>
          <CardHeader className="pb-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base">Bolões do concurso</CardTitle>
                <Badge variant="secondary">
                  {selectedPools.length} de {selectablePools.length} selecionado(s)
                </Badge>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleSelectAll}>
                  Selecionar todos
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={GHOST_BUTTON_CLASS}
                  onClick={handleClearSelection}
                >
                  Desmarcar todos
                </Button>
              </div>
            </div>
            <p className="pt-1 text-sm text-muted-foreground">
              {poolsData.seller.name}
              {poolsData.seller.unity_name ? ` • ${poolsData.seller.unity_name}` : ""} —
              concurso {poolsData.contest.name}
              {poolsData.contest.status_name ? ` (${poolsData.contest.status_name})` : ""}
            </p>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {poolsData.pools.map((pool) => {
                const isSelected = selectedPools.includes(pool.cp_classification_id);

                return (
                  <button
                    key={pool.cp_classification_id}
                    type="button"
                    onClick={() => togglePool(pool)}
                    disabled={!pool.selectable}
                    className={cn(
                      "flex items-start gap-2 rounded-md border p-3 text-left transition-colors",
                      pool.selectable
                        ? "hover:bg-accent"
                        : "cursor-not-allowed opacity-60",
                      isSelected && "border-primary bg-primary/5"
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={!pool.selectable}
                      className="mt-0.5 pointer-events-none"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">
                        {pool.product_name}
                      </div>
                      {pool.product_classification_name && (
                        <div className="truncate text-xs text-muted-foreground">
                          {pool.product_classification_name}
                        </div>
                      )}
                      <div className="mt-1 flex flex-wrap gap-1 text-xs">
                        <Badge variant="secondary">Bal. {pool.balance}</Badge>
                        <Badge variant="outline">Preso {pool.with_held_balance}</Badge>
                      </div>
                      {pool.has_duplicates && (
                        <div className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                          {pool.rows_count} linhas duplicadas — consolide antes
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {poolsData && poolsData.pools.length > 0 && (
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">Correção</CardTitle>
            </div>
            <p className="pt-1 text-sm text-muted-foreground">
              A mesma configuração é aplicada a todos os bolões selecionados.
            </p>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {renderFieldConfig("Balanço", balanceConfig, setBalanceConfig)}
              {renderFieldConfig("Valor preso", withHeldConfig, setWithHeldConfig)}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
              {validationError && (
                <span className="mr-auto text-xs text-amber-700 dark:text-amber-400">
                  {validationError}
                </span>
              )}
              <Button
                size="sm"
                onClick={handlePreview}
                disabled={Boolean(validationError) || isPreviewing}
              >
                {isPreviewing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ClipboardList className="mr-2 h-4 w-4" />
                )}
                Gerar resumo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {preview && (
        <Card>
          <CardHeader className="pb-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">Resumo da correção</CardTitle>
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="secondary">{preview.pools_selected} bolões</Badge>
                <Badge variant="outline">{preview.pools_changed} com alteração</Badge>
              </div>
            </div>
            <div className="pt-1 text-sm text-muted-foreground">
              {preview.seller.name} — concurso {preview.contest.name}
              <br />
              Balanço:{" "}
              <strong>
                {OPERATION_LABELS[preview.operations.balance.operation]}
                {preview.operations.balance.amount !== null
                  ? ` ${preview.operations.balance.amount}`
                  : ""}
              </strong>{" "}
              • Valor preso:{" "}
              <strong>
                {OPERATION_LABELS[preview.operations.with_held_balance.operation]}
                {preview.operations.with_held_balance.amount !== null
                  ? ` ${preview.operations.with_held_balance.amount}`
                  : ""}
              </strong>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="overflow-hidden rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bolão</TableHead>
                    <TableHead className="text-right">Balanço atual</TableHead>
                    <TableHead className="text-right">Balanço esperado</TableHead>
                    <TableHead className="text-right">Preso atual</TableHead>
                    <TableHead className="text-right">Preso esperado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.items.map((item) => (
                    <TableRow
                      key={item.cp_classification_id}
                      className={item.changed ? "" : "opacity-60"}
                    >
                      <TableCell className="font-medium">{item.product_name}</TableCell>
                      <TableCell className="text-right">{item.current_balance}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {item.new_balance}
                      </TableCell>
                      <TableCell className="text-right">
                        {item.current_with_held_balance}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {item.new_with_held_balance}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
              Total dos bolões selecionados —{" "}
              <strong>{preview.totals_before.balance}</strong> →{" "}
              <strong>{preview.totals_after.balance}</strong> de balanço e{" "}
              <strong>{preview.totals_before.with_held_balance}</strong> →{" "}
              <strong>{preview.totals_after.with_held_balance}</strong> de valor preso.
              Os valores acima foram recalculados pelo servidor. Se algum saldo mudar
              antes da confirmação, a operação será recusada.
            </div>

            {isConfirming ? (
              <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
                <span className="mr-auto text-sm font-medium">
                  Confirma a correção de {preview.pools_changed} bolão(ões)?
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsConfirming(false)}
                  disabled={isApplying}
                >
                  Cancelar
                </Button>
                <Button size="sm" onClick={handleApply} disabled={isApplying}>
                  {isApplying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Confirmar correção
                </Button>
              </div>
            ) : (
              <div className="flex justify-end border-t pt-4">
                <Button size="sm" onClick={() => setIsConfirming(true)}>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Aplicar correção em lote
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {result && (
        <Card className="border-emerald-300 dark:border-emerald-800">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <CardTitle className="text-base">Resultado da operação</CardTitle>
            </div>
            <p className="pt-1 text-sm text-muted-foreground">
              {result.seller.name} — concurso {result.contest.name}
            </p>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm lg:grid-cols-4">
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Bolões selecionados</div>
                <div className="text-lg font-semibold">{result.pools_selected}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Bolões alterados</div>
                <div className="text-lg font-semibold">{result.pools_changed}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Balanço antes → depois</div>
                <div className="text-lg font-semibold">
                  {result.totals_before.balance} → {result.totals_after.balance}
                </div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs text-muted-foreground">Preso antes → depois</div>
                <div className="text-lg font-semibold">
                  {result.totals_before.with_held_balance} →{" "}
                  {result.totals_after.with_held_balance}
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bolão</TableHead>
                    <TableHead>Linha</TableHead>
                    <TableHead className="text-right">Balanço</TableHead>
                    <TableHead className="text-right">Valor preso</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.items.map((item) => (
                    <TableRow key={item.cp_classification_id}>
                      <TableCell className="font-medium">{item.product_name}</TableCell>
                      <TableCell className="font-mono text-xs">#{item.row_id}</TableCell>
                      <TableCell className="text-right">
                        {item.current_balance} → <strong>{item.new_balance}</strong>
                      </TableCell>
                      <TableCell className="text-right">
                        {item.current_with_held_balance} →{" "}
                        <strong>{item.new_with_held_balance}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="text-sm text-muted-foreground">
              Validação pós-execução:{" "}
              <strong className="text-emerald-700 dark:text-emerald-400">aprovada</strong>
              . Cada alteração foi registrada na aba Auditoria.
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
